import CssBaseline from "@mui/material/CssBaseline";
import InitColorSchemeScript from "@mui/material/InitColorSchemeScript";
import { ThemeProvider } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useRoutes } from "react-router";
import { Loader } from "./components/loader.tsx";
import { config } from "./config/index.ts";
import { AlertProvider } from "./context/AlertContext.tsx";
import { GlobalErrorBoundary } from "./GlobalErrorBoundary.tsx";
import "./lib/api/setupInterceptors"; // Initialize API interceptors
import { initClarity } from "./lib/observability/clarity.ts";
import { ObservabilityProvider } from "./lib/observability/hooks.tsx";
import { observability } from "./lib/observability/index.ts";
import { QueryClientProvider, queryClient, ReactQueryDevtools } from "./lib/react-query/index.ts";
import { preloadPDFNet } from "./lib/webviewer/pdfAnnotationExtractor.ts";
import { AuthProvider } from "./providers/authProvider/index.tsx";
import { routes } from "./routes.tsx";
import { figma } from "./themes/figma/figma.ts";

// Initialize MUI X license
LicenseInfo.setLicenseKey(config.dataGridPro.licenseKey);

const { apm } = observability.start(config.ilEnv);

// Initialize Clarity analytics (skipped in local to avoid COEP/CORS errors)
initClarity(config.ilEnv);

// Preload PDFNet during browser idle time (non-blocking)
preloadPDFNet(config.pdfTron.licenseKey);

function AppRoutes() {
  return <Suspense fallback={<Loader />}>{useRoutes(routes)}</Suspense>;
}

async function enableMocking() {
  if (!import.meta.env.VITE_MSW_ENABLED) {
    return;
  }

  const { worker } = await import("./mocks/browser");
  // In sandbox mode warn about unhandled requests so missing stubs are visible.
  // In regular dev:msw mode bypass silently (real services handle the rest).
  const onUnhandledRequest = import.meta.env.VITE_SANDBOX ? "warn" : "bypass";

  if (import.meta.env.VITE_SANDBOX) {
    const { broadcastPersona, mountControlPanel } = await import(
      "./mocks/sandbox/sandboxBootstrap"
    );
    broadcastPersona(); // must happen before worker.start() so SW sees persona on first request
    await worker.start({ onUnhandledRequest });
    await mountControlPanel(); // after worker is active
    return;
  }

  await worker.start({ onUnhandledRequest });
}

enableMocking().then(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <InitColorSchemeScript attribute="data" />
      <ThemeProvider theme={figma} forceThemeRerender>
        <CssBaseline />
        <ObservabilityProvider apm={apm}>
          <GlobalErrorBoundary apm={apm}>
            <AlertProvider>
              <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools initialIsOpen={false} />
                <BrowserRouter>
                  <AuthProvider>
                    <AppRoutes />
                  </AuthProvider>
                </BrowserRouter>
              </QueryClientProvider>
            </AlertProvider>
          </GlobalErrorBoundary>
        </ObservabilityProvider>
      </ThemeProvider>
    </StrictMode>,
  );
});
